import * as React from 'react';
import {
  filter, isEmpty, isNull, map,
} from 'lodash';
import {
  Typography,
  Spinner,
} from '@revfluence/fresh';
import moment from 'moment';
import { IRowData } from '@components';

import { OFFER_SOURCE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { ExclamationCircleFilled } from '@ant-design/icons';
import cx from 'classnames';
import { isBefore } from 'date-fns';
import { SpinnerThirdIcon } from '@revfluence/fresh-icons/solid/esm';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { AUTOMATED_PAYOUT_ID, AutomatedPayoutLabel } from '@frontend/applications/AffiliatesApp/constants/offers';
import { Input } from '@frontend/shadcn/components/ui/input';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2/Table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@frontend/shadcn/components/ui/select';
import {
  TooltipHeader,
  TooltipTitle,
} from '@frontend/applications/Social/components/tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import { isString } from 'lodash';
import { TriangleExclamationIcon } from '@revfluence/fresh-icons/solid/esm';
import { createUrlObj } from '@frontend/applications/AffiliatesApp/utils';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Button } from '@frontend/shadcn/components/ui/button';
import { CalendarIcon } from '@revfluence/fresh-icons/solid/esm';
import { format } from 'date-fns';
import ShadCnAlert from '@frontend/app/refresh-components/Alert';
import {
  TActiveDates,
  TMode,
  TSelectedMember,
} from '../../types';

import {
  cleanPromoCode,
  getMemberOfferStatus,
  isOfferShopify,
  isOfferTune,
} from '../../utils';
import styles from '../MembersWizard.scss';
import { IPayoutVariant } from '../../../OfferForm';

const { useMemo } = React;

const TABLE_WIDTH = 900;

function getShopifyRefreshColumns() {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate w-[120px]">
              {row.original.name.length > 30
                ? `${row.original.name.substring(0, 30)}...`
                : row.original.name}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px]">
            <TooltipHeader>
              <TooltipTitle>{row.original.name}</TooltipTitle>
            </TooltipHeader>
          </TooltipContent>
        </Tooltip>
      ),
      size: 240,
      minSize: 240,
      maxSize: 240,
    },
    {
      accessorKey: 'offerName',
      header: 'Offer',
      cell: ({ row }) => row.original.offerName,
      size: Math.floor(TABLE_WIDTH / 4),
      minSize: Math.floor(TABLE_WIDTH / 4),
      maxSize: Math.floor(TABLE_WIDTH / 4),
    },
    {
      accessorKey: 'previousCode',
      header: 'Previous Code',
      cell: ({ row }) => row.original.previousCode,
      size: Math.floor(TABLE_WIDTH / 4),
      minSize: Math.floor(TABLE_WIDTH / 4),
      maxSize: Math.floor(TABLE_WIDTH / 4),
    },
    {
      accessorKey: 'code',
      header: 'Potential Code',
      cell: ({ row }) => row.original.code,
      size: Math.floor(TABLE_WIDTH / 4),
      minSize: Math.floor(TABLE_WIDTH / 4),
      maxSize: Math.floor(TABLE_WIDTH / 4),
    },
  ];
}

function getTuneRefreshColumns() {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate w-[120px]">
              {row.original.name.length > 30
                ? `${row.original.name.substring(0, 30)}...`
                : row.original.name}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px]">
            <TooltipHeader>
              <TooltipTitle>{row.original.name}</TooltipTitle>
            </TooltipHeader>
          </TooltipContent>
        </Tooltip>
      ),
      size: 240,
      minSize: 240,
      maxSize: 240,
    },
    {
      accessorKey: 'offerName',
      header: 'Offer',
      cell: ({ row }) => row.original.offerName,
      size: Math.floor(TABLE_WIDTH / 2),
      minSize: Math.floor(TABLE_WIDTH / 2),
      maxSize: Math.floor(TABLE_WIDTH / 2),
    },
  ];
}

interface IProps {
  activeDates?: TActiveDates;
  members: readonly TSelectedMember[] | null;
  mode: TMode;
  offerName: string;
  offerSource: OFFER_SOURCE;
  isWorkflow: boolean;
  deepLinkColumn?: boolean;
  updateMemberCode?: (memberId: string, field: string, value: string) => void;
  updateMemberPayoutId?: (memberId: string, field: string, value: number | null) => void;
  isEditablerow?: boolean
  isNewFlow: boolean
  payouts?: IPayoutVariant[] | null;
  defaultPayoutId?: number | null;
  isUngrouped?: boolean;
  bulkPayoutId?: number;
  bulkStartDate?: Date,
  bulkEndDate?: Date;
  offerStartDate?: string;
  updateMemberActiveDate?: (memberId: number, field: string, value: Date) => void;
  isSaving?: boolean;
  isAutomatedPayoutEnabled?: boolean;
  updateMemberDeepLink?: (memberId: string, deepLink: string, focused: boolean) => void;
  offerLink?: string;
}
export const RefreshReviewMembers: React.FC<Readonly<IProps>> = (props) => {
  const {
    activeDates,
    members,
    mode,
    isWorkflow,
    offerName,
    offerSource,
    payouts = [],
    defaultPayoutId,
    isUngrouped,
    bulkPayoutId,
    offerStartDate,
    isSaving = false,
    isAutomatedPayoutEnabled,
    offerLink,
  } = props;
  const isShopify = isOfferShopify(offerSource);
  const isTune = isOfferTune(offerSource);
  const { isProjects, migrateToGraphQL, automatePayoutTier } = useClientFeatures();
  const programLabel = isProjects ? 'Project' : 'Program';
  const data = useMemo(() => map(members, (m): IRowData => {
    let errorMessage = '';
    if (m.selected) {
      if (props.mode === 'add' && isEmpty(m.code)) {
        errorMessage = 'Manual entry needed due to missing member details.';
      } else if (m.isDuplicate) {
        errorMessage = 'Code must be unique.';
      }
    }
    switch (mode) {
      case 'add': {
        let payoutId = null;
        let addData = null;
        let startDate: { startDate: Date, onChange: (date: moment.Moment) => void } = null;
        let endDate: { endDate: Date, onChange: (date: moment.Moment) => void } = null;
        if (migrateToGraphQL && defaultPayoutId) {
          payoutId = {
            payoutId: bulkPayoutId || m.payoutId,
            onChange: (event) => {
              props.updateMemberPayoutId(m.id.toString(), 'payoutId', event);
            },
          };
          if (isUngrouped) {
            startDate = {
              startDate: m?.startDate,
              onChange: (date: moment.Moment) => {
                props.updateMemberActiveDate(m.id, 'startDate', date ? date.toDate() : null);
              },
            };
            endDate = {
              endDate: m?.endDate,
              onChange: (date: moment.Moment) => {
                props.updateMemberActiveDate(m.id, 'endDate', date ? date.toDate() : null);
              },
            };
          }
        }
        if (props.isNewFlow) {
          let code = null;
          if (!m.forceCheckIn) {
            code = {
              code: m.code,
              errorMessage,
              onChange: (event) => {
                const input = event.target;
                const cursor = input.selectionStart;
                const preCursor = cleanPromoCode(input.value.slice(0, cursor));
                const postCursor = cleanPromoCode(input.value.slice(cursor));
                const code = `${preCursor}${postCursor}`;
                if (code !== m.code) {
                  props.updateMemberCode(m.id.toString(), 'code', code);
                }
                setTimeout(() => {
                  input.selectionStart = preCursor.length;
                  input.selectionEnd = preCursor.length;
                }, 0);
              },
            };
          }
          addData = {
            code,
            payoutId: {
              payoutId: m.payoutId ?? defaultPayoutId,
              onChange: (value) => {
                props.updateMemberPayoutId(m.id.toString(), 'payoutId', value);
              },
            },
            startDate,
            endDate,
            id: m.id.toString(),
            isDuplicate: m.selected && m.isDuplicate,
            name: m.name,
            instagramUsername: m.instagramUsername,
            offerStatus: getMemberOfferStatus(m, isWorkflow, offerSource),
            programs: m.selectedPrograms,
            _bodyRowClassName: cx({
              [styles.codeHasError]: m.selected && (isEmpty(m.code) || m.isDuplicate),
            }),
            disableSelection: m.forceCheckIn,
            deepLink: { ...m.deepLink, memberId: m.id.toString() },
            _raw: m,
          };
        } else {
          addData = {
            code: m.code,
            payoutId,
            id: m.id.toString(),
            name: m.name,
            offerStatus: getMemberOfferStatus(m, isWorkflow, offerSource),
            programs: m.selectedPrograms,
            instagramUsername: m.instagramUsername,
            disableSelection: m.forceCheckIn,
            deepLink: { ...m.deepLink, memberId: m.id.toString() },
            _raw: m,
          };
        }
        if (!migrateToGraphQL && !defaultPayoutId) {
          delete addData.payoutId;
        }
        return addData;
      }
      case 'refresh':
        return {
          code: m.code,
          id: m.id.toString(),
          key: m.id.toString(),
          name: m.name,
          offerName: m.forceCheckIn ? getMemberOfferStatus(m, isWorkflow, offerSource) : offerName,
          previousCode: m.previousCode.code,
          _raw: m,
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [activeDates, members, mode, offerName]);
  const payoutOptions: { id: number | null, label: string }[] = useMemo(() => {
    if (automatePayoutTier && isAutomatedPayoutEnabled) {
      const automatedPayouts = payouts.filter((payout) => typeof payout.automationThreshold === 'number' && payout.automationThreshold >= 0);
      const regularPayouts = payouts.filter((payout) => payout.automationThreshold === undefined || payout.automationThreshold === null);

      const options = automatedPayouts.length > 0
        ? [{ id: AUTOMATED_PAYOUT_ID, label: AutomatedPayoutLabel }]
        : [];

      regularPayouts.forEach((payout) => {
        const rules = (payout as any).offerPayoutRules || payout.payoutRules || [];
        let option = null;
        if (rules.length > 0) {
          option = { id: payout.id, label: `${payout.label} (Multiple)` };
        } else if (!isNull(payout.percentPayout) && !isNull(payout.flatPayout)) {
          option = { id: payout.id, label: `${payout.label} (${payout.percentPayout}% + $${payout.flatPayout})` };
        } else if (!isNull(payout.flatPayout)) {
          option = { id: payout.id, label: `${payout.label} ($${payout.flatPayout})` };
        } else if (!isNull(payout.percentPayout)) {
          option = { id: payout.id, label: `${payout.label} (${payout.percentPayout}%)` };
        }
        if (option) options.push(option);
      });

      return options;
    } else {
      return payouts.map((payout) => {
        const rules = (payout as any).offerPayoutRules || payout.payoutRules || [];
        if (rules.length > 0) {
          return { id: payout.id, label: `${payout.label} (Multiple)` };
        } else if (!isNull(payout.percentPayout) && !isNull(payout.flatPayout)) {
          return { id: payout.id, label: `${payout.label} (${payout.percentPayout}% + $${payout.flatPayout})` };
        } else if (!isNull(payout.flatPayout)) {
          return { id: payout.id, label: `${payout.label} ($${payout.flatPayout})` };
        } else if (!isNull(payout.percentPayout)) {
          return { id: payout.id, label: `${payout.label} (${payout.percentPayout}%)` };
        }
        return null;
      }).filter(Boolean);
    }
  }, [payouts, automatePayoutTier, isAutomatedPayoutEnabled]);

  const columnConfigNew = useMemo(() => {
    switch (mode) {
      case 'add':
        const columns = [
          {
            accessorKey: 'name',
            header: 'Name',
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate w-[120px]">
                    {row.original.name.length > 30
                      ? `${row.original.name.substring(0, 30)}...`
                      : row.original.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px]">
                  <TooltipHeader>
                    <TooltipTitle>{row.original.name}</TooltipTitle>
                  </TooltipHeader>
                </TooltipContent>
              </Tooltip>
            ),
            size: 240,
            minSize: 240,
            maxSize: 240,
          },
        ];

        if (isShopify) {
          columns.push({
            accessorKey: 'code',
            header: 'Potential Code',
            cell: ({ row }) => {
              const data = row.original.code;
              if (!data) {
                return null;
              }
              return (
                <div className="flex items-center gap-2">
                  <Input
                    className={cx(
                      {
                        '!border-red-4 focus-visible:ring-red-4': data.errorMessage
                      }
                    )}
                    onChange={data.onChange}
                    placeholder="TYPE CODE HERE"
                    value={data.code}
                    disabled={!props.isEditablerow}
                  />
                  {data.errorMessage && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <ExclamationCircleFilled className="text-red-4" />
                      </TooltipTrigger>
                      <TooltipContent
                        className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[270px]"
                        side="top"
                        align="end"
                      >
                        <TooltipHeader>
                          <TooltipTitle>{data.errorMessage}</TooltipTitle>
                        </TooltipHeader>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              );
            },
            size: 200,
            minSize: 200,
            maxSize: 200,
          });
        }

        if (isTune && props.deepLinkColumn) {
          const offerURL = createUrlObj(offerLink);
          if (!isString(offerURL)) {
            columns.push({
              accessorKey: 'deepLink',
              header: 'Deep Link',
              cell: ({ row }) => {
                const data = row.original.deepLink;
                const hasError = data.error && row.original._raw.selected && !data.focused;
                const hasWarning = data.warning && row.original._raw.selected && !data.focused;
                if (!row.original._raw.selected) {
                  return <Typography.Text type="secondary">N/A</Typography.Text>;
                }
                return (
                  <div className="relative flex items-center">
                    <Input
                      className={cx(
                        'w-full',
                        {
                          'border-red-500 focus-visible:ring-red-500': hasError,
                          'border-yellow-500 focus-visible:ring-yellow-500': !hasError && hasWarning,
                        }
                      )}
                      onChange={(e) => props.updateMemberDeepLink(data.memberId, e.target.value, true)}
                      placeholder="Enter URL"
                      disabled={!row.original._raw.selected}
                      value={data.link}
                      onBlur={(e) => props.updateMemberDeepLink(data.memberId, e.target.value, false)}
                    />
                    {(data.warning || data.error) && row.original._raw.selected && !data.focused && (
                      <div className="absolute right-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="cursor-help">
                              <TriangleExclamationIcon className={cx(
                                'h-4 w-4',
                                {
                                  'text-red-500': hasError,
                                  'text-yellow-500': !hasError && hasWarning,
                                }
                              )}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[270px]"
                            side="top"
                            align="end"
                          >
                            <TooltipHeader>
                              <TooltipTitle>{data.error || data.warning}</TooltipTitle>
                            </TooltipHeader>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              },
              size: Math.floor(TABLE_WIDTH / 4),
              minSize: Math.floor(TABLE_WIDTH / 4),
              maxSize: Math.floor(TABLE_WIDTH / 4),
            });
          }
        }

        if (defaultPayoutId) {
          columns.push({
            accessorKey: 'payoutId',
            header: 'Payout',
            cell: ({ row }) => {
              const data = row.original.payoutId;
              const payoutOptionIds = payoutOptions.map((opt) => opt.id?.toString());
              const rawValue = automatePayoutTier && isAutomatedPayoutEnabled
                ? data?.payoutId ?? AUTOMATED_PAYOUT_ID
                : data?.payoutId ?? defaultPayoutId;
              const valueString = rawValue !== undefined && rawValue !== null ? rawValue.toString() : undefined;
              const selectValue = payoutOptionIds.includes(valueString) ? valueString : undefined;
              return (
                <Select
                  value={selectValue}
                  onValueChange={data.onChange}
                  disabled={!props.isEditablerow}
                >
                  <SelectTrigger className="w-full !bg-grey-2">
                    <SelectValue placeholder="Select payout" />
                  </SelectTrigger>
                  <SelectContent>
                    {payoutOptions.map((payout) => (
                      <SelectItem key={payout.id.toString()} value={payout.id.toString()}>
                        {payout.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            },
            size: 200,
            minSize: 200,
            maxSize: 200,
          });
          if (isUngrouped) {
            columns.push({
              accessorKey: 'startDate',
              header: 'Offer Start Date',
              cell: ({ row }) => {
                const data = row.original.startDate;
                const disableStartDate = (current) => {
                  if (!offerStartDate) {
                    return true;
                  }
                  const startDate = new Date(offerStartDate);
                  return current && current < startDate;
                };
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cx(
                          'w-full justify-start text-left font-normal',
                          !data?.startDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {data?.startDate ? format(data.startDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 pointer-events-auto select-text" align="start">
                      <Calendar
                        mode="single"
                        selected={data?.startDate}
                        onSelect={(date) => data.onChange(moment(date))}
                        disabled={(date) => disableStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                );
              },
              size: 200,
              minSize: 200,
              maxSize: 200,
            });
            columns.push({
              accessorKey: 'endDate',
              header: 'Offer End Date (Optional)',
              cell: ({ row }) => {
                const data = row.original.endDate;
                const disableEndDate = (current) => {
                  let disableDateTill = row.original.startDate?.startDate;
                  if (!disableDateTill) {
                    return true;
                  }
                  disableDateTill = isBefore(new Date(row.original.startDate.startDate), new Date()) ? new Date() : row.original.startDate.startDate;
                  const startDate = new Date(disableDateTill);
                  return current && current < startDate;
                };
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cx(
                          'w-full justify-start text-left font-normal',
                          !data?.endDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {data?.endDate ? format(data.endDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 pointer-events-auto select-text " align="start">
                      <Calendar
                        mode="single"
                        selected={data?.endDate}
                        onSelect={(date) => data.onChange(moment(date))}
                        disabled={(date) => disableEndDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                );
              },
              size: 200,
              minSize: 200,
              maxSize: 200,
            });
          }
        }
        return columns;
      case 'refresh': {
        if (isShopify) return getShopifyRefreshColumns();
        if (isTune) return getTuneRefreshColumns();
        return [];
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, programLabel, isShopify, isTune]);

  const getMissingCodesAlert = (
    mode: IProps['mode'],
    offerSource: IProps['offerSource'],
    members: IProps['members'],
  ): React.ReactElement => {
    if (mode !== 'add' || offerSource !== OFFER_SOURCE.SHOPIFY) {
      return null;
    }
    const missingCodeCount = filter(members, (m) => m.selected && m.code.length < 1).length;
    if (missingCodeCount < 1) {
      return null;
    }
    const message = `
        ${missingCodeCount.toLocaleString()} member${missingCodeCount !== 1 ? 's' : ''}
        do${missingCodeCount !== 1 ? '' : 'es'} not have the data required to generate
        a promo code. You can manually enter a promo code for them or leave them
        out of this offer for the time being.
      `.trim();
    return (
      <ShadCnAlert
        title="Missing Code"
        description={message}
        type="error"
      />
    );
  };

  const getDuplicateCodesAlert = (members: IProps['members']): React.ReactElement => {
    const duplicateCodeCount = filter(members, (m) => m.selected && m.isDuplicate).length;
    if (duplicateCodeCount < 1) {
      return null;
    }
    const message = `
      ${duplicateCodeCount.toLocaleString()} members will have the same code.
      Each member must have a unique promo code so Shopify can properly
      attribute conversion data to the member.
    `.trim();
    return (
      <ShadCnAlert
        title="Duplicate Code"
        description={message}
        type="error"
      />
    );
  };

  const getActiveDatesAlerts = (members: IProps['members']): React.ReactElement => {
    const invalidDateCount = (members ?? []).filter((m) => !m.startDate || (m.startDate && m.endDate && isBefore(m.endDate, m.startDate))).length;
    if (invalidDateCount < 1) {
      return null;
    }
    const message = `
      ${invalidDateCount.toLocaleString()} members have wrong dates.
    `.trim();
    return (
      <ShadCnAlert
        title="Wrong Dates"
        description={message}
        type="error"
      />
    );
  };
  if (isSaving) {
    return <Spinner className={styles.loading} indicator={<SpinnerThirdIcon style={{ fontSize: '32px' }} />} />;
  }
  return (
    <div className="p-4 space-y-4">
      <p className="text-sm text-grey-4 m-0 font-normal">Review the members who will receive the codes/links for this promotion.</p>
      {getMissingCodesAlert(props.mode, props.offerSource, members)}
      {getDuplicateCodesAlert(members)}
      {props.isUngrouped && (
        getActiveDatesAlerts(members)
      )}
      <div className="overflow-y-auto border-t border-l border-r rounded-xl">
        <DataTable
          columns={columnConfigNew}
          data={data}
          className="border-t"
        />
      </div>
    </div>
  );
};
